<template>
  <div v-show="!!menu" ref="menuRef" class="rec-status-filter-menu" :style="menuStyle" @click.stop>
    <label class="fcp-check">
      <input type="checkbox" :checked="allSelected" @change="$emit('toggle-all')" />
      Select all
    </label>
    <label v-for="option in options" :key="option" class="fcp-check">
      <input
        type="checkbox"
        :checked="selectedOptions.includes(option)"
        @change="$emit('toggle-option', option)"
      />
      {{ option }}
    </label>
  </div>
</template>

<script>
import { defineComponent, ref, computed, nextTick, onMounted, onBeforeUnmount } from '@vue/composition-api'

const VIEWPORT_MARGIN = 8

// Status?'s own column-header filter (see RecProductionReport.vue's #head(variance_status)) -
// same portal-to-<body>/manual-positioning/single-shared-instance technique as
// RecColumnSearchMenu and RecInlineOptionsMenu (see their own top comments for why: this open/
// closed state must never live as a ref on RecProductionReport itself, or it forces a full
// re-render of every row/cell in the Variance Transaction Details table). Unlike those two, only
// one column ever needs this (Status? alone), so there's no per-column/per-row targetId to track -
// a plain open/closed boolean is enough. The actual filter state (approvalStatusFilter) stays on
// RecProductionReport and flows down as props/events, exactly like columnSearch does for
// RecColumnSearchMenu - this component only owns whether the checklist is visible and where.
export default defineComponent({
  name: 'rec-status-filter-menu',
  props: {
    options: {
      type: Array,
      default: () => []
    },
    selectedOptions: {
      type: Array,
      default: () => []
    },
    allSelected: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const menu = ref(null)
    const menuRef = ref(null)

    const menuStyle = computed(() =>
      menu.value ? { top: `${menu.value.top}px`, left: `${menu.value.left}px` } : {}
    )

    const close = () => {
      menu.value = null
    }

    // Two-pass, same technique as RecColumnSearchMenu's own toggle(): an initial guess (right
    // below the search icon) renders the checklist so its real size can be measured, then a
    // second pass clamps it against the viewport.
    const toggle = async (event) => {
      if (menu.value) {
        close()
        return
      }

      const rect = event.currentTarget.getBoundingClientRect()
      menu.value = { top: rect.bottom + 2, left: rect.left }

      await nextTick()

      if (!menuRef.value || !menu.value) return

      const menuRect = menuRef.value.getBoundingClientRect()
      const margin = VIEWPORT_MARGIN

      let left = rect.left
      left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin)

      let top = rect.bottom + 2
      if (top + menuRect.height + margin > window.innerHeight) {
        top = rect.top - menuRect.height - 2
      }
      top = Math.max(margin, top)

      menu.value = { top, left }
    }

    const onDocumentMousedown = (event) => {
      if (!menu.value) return
      if (menuRef.value && menuRef.value.contains(event.target)) return
      if (event.target.closest && event.target.closest('.fcp-th-search-toggle')) return
      close()
    }

    const onScroll = () => {
      if (menu.value) close()
    }

    const onKeydown = (event) => {
      if ('Escape' === event.key && menu.value) close()
    }

    onMounted(() => {
      if (menuRef.value) document.body.appendChild(menuRef.value)
      document.addEventListener('mousedown', onDocumentMousedown)
      window.addEventListener('scroll', onScroll, true)
      document.addEventListener('keydown', onKeydown)
    })

    onBeforeUnmount(() => {
      if (menuRef.value && document.body.contains(menuRef.value)) {
        document.body.removeChild(menuRef.value)
      }
      document.removeEventListener('mousedown', onDocumentMousedown)
      window.removeEventListener('scroll', onScroll, true)
      document.removeEventListener('keydown', onKeydown)
    })

    return {
      menu,
      menuRef,
      menuStyle,
      toggle
    }
  }
})
</script>

<style lang="scss" scoped>
.rec-status-filter-menu {
  position: fixed;
  z-index: 2000;
  width: 160px;
  min-width: 160px;
  max-width: 160px;
  max-height: 200px;
  overflow-y: auto;
  padding: 0.3rem 0.5rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  font-size: 0.65rem;
  color: #000;

  .fcp-check {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    margin-bottom: 0.25rem;
    cursor: pointer;
    white-space: nowrap;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>
